// src/pages/about/controllers/About.controller.tsx
import { useState, useEffect } from "react";
import { AboutViewProps } from "../types";
import cassioImage from "../../../assets/cassio.jpeg";
import { useEducation } from "../../../hooks/useEducation";
import { useExperiences } from "../../../hooks/useExperiences";
import { useDocumentTitle } from "../../../hooks/useDocumentTitle";

export function AboutController() {
  useDocumentTitle(
    "Sobre",
    "Sobre Cassio Silva Takarada — formação, experiência profissional e certificações."
  );
  const [isLoading, setIsLoading] = useState<boolean>(true);
  
  // Hooks para buscar dados da API
  const { education: educationData, loading: educationLoading } = useEducation({ limit: 100 });
  const { experiences: experiencesData, loading: experiencesLoading } = useExperiences({ limit: 100 });

  // Simula carregamento inicial
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  // Educação (fallback hardcoded)
  const educationFallback = [
    {
      id: 1,
      institution: "USP - Universidade de São Paulo",
      degree: "Master's degree",
      field: "Computational and Applied Mathematics",
      period: "Jul 2024 - Jul 2026",
      description:
        "Projeto sobre análise de séries temporais para explorar e otimizar modelos de deep learning em imagens médicas, inspirado pelo trabalho do Dr. Luiz Otávio Murta Junior, visando avançar a IA na saúde. O foco está na complexidade estatística de redes de deep learning aplicadas a imagens médicas, identificando e otimizando arquiteturas e métodos de treino para tarefas como segmentação e apoio diagnóstico.",
      logo: "/assets/education/usp_logo.png",
      skills: [
        "Analytical Skills",
        "Data Analysis",
        "Python",
        "Machine Learning",
        "Artificial Intelligence",
        "Artificial Neural Networks",
        "Scientific Computing",
      ],
    },
    {
      id: 2,
      institution: "The University of Queensland",
      degree: "Bachelor's degree",
      field: "Chemical Engineering",
      period: "Jan 2015 - Dec 2015",
      description:
        "Programa de intercâmbio do governo brasileiro: 6 meses de Inglês Acadêmico e 1 ano de Engenharia Química. Atividades e sociedades: ICTE Choir.",
      logo: "/assets/education/uq_logo.png",
      skills: ["Communication"],
    },
    {
      id: 3,
      institution:
        'Fatec Franca - Faculdade de Tecnologia "Dr. Thomaz Novelino"',
      degree: "Bachelor's degree",
      field: "Cross-platform software development",
      period: "Feb 2022 - Dec 2024",
      description:
        "Projetos: (1) Site estático de delivery com HTML e CSS; (2) Projeto fullstack de clínica veterinária com agendamento (Node.js, React.js, MongoDB); (3) Sistema IoT de irrigação automática com ESP32, Node.js, React.js, React Native e MongoDB para web e mobile.",
      logo: "/assets/education/fatec_franca_logo.png",
      skills: [
        "Front-End Development",
        "Data Analysis",
        "React.js",
        "Software Engineering",
        "Artificial Intelligence",
        "Statistics",
        "Node.js",
        "Software Engineering Practices",
        "CSS",
        "Machine Learning",
        "Back-End Web Development",
        "Internet of Things (IoT)",
        "HTML5",
      ],
    },
    {
      id: 4,
      institution: "Universidade Federal de Uberlândia - UFU",
      degree: "Bachelor’s Degree",
      field: "Chemical Engineering",
      period: "2011 - 2019",
      description:
        "Uma das melhores faculdades de Engenharia Química do Brasil, com currículo diverso e bem estruturado e atividades extracurriculares que desenvolvem habilidades pessoais e profissionais. Participações: PET (bolsa de pesquisa, ensino e extensão), Diretório Acadêmico (diretor acadêmico), Empresa Júnior de Engenharia Química (Marketing, Vendas e Finanças) e Núcleo das Empresas Juniores (Coordenador de Comunicação). CRA geral: 72.071.",
      logo: "/assets/education/ufu_logo.png",
      skills: [
        "Presentations",
        "Time Management",
        "C++",
        "Leadership",
        "Innovation Management",
        "Agile Methodologies",
        "Analytical Skills",
        "Group Work",
        "C#",
        "Statistics",
        "Scrum",
        "Problem Solving",
        "Adaptability",
        "Strategy",
        "Kanban",
        "Communication",
        "Attention to Detail",
        "Organization Skills",
      ],
    },
    {
      id: 5,
      institution: "Universidade Cruzeiro do Sul",
      degree: "Teaching credential",
      field: "Mathematics",
      period: "Aug 2019 - Sep 2020",
      description:
        "Complemento à graduação em Engenharia Química para habilitação ao ensino em escolas públicas no Brasil.",
      logo: "/assets/education/cruzeiro_do_sul_logo.png",
      skills: [
        "Data Analysis",
        "Analytical Skills",
        "Problem Solving",
        "Attention to Detail",
      ],
    },
  ];
  
  // Usa dados da API se disponíveis, senão fallback
  const education = educationData.length > 0 ? educationData.map((edu: any) => ({
    id: edu._id,
    institution: edu.institution,
    degree: edu.degree,
    field: edu.field,
    period: `${new Date(edu.periodStart).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })} - ${edu.periodEnd ? new Date(edu.periodEnd).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : 'Present'}`,
    description: edu.description,
    logo: edu.logo || '/assets/education/default_logo.png',
    skills: edu.skills || [],
  })) : educationFallback;

  // Experiência (fallback hardcoded)
  const experienceFallback = [
    {
      id: 1,
      company: "GRAN",
      role: "Full Stack Engineer",
      period: "Dec 2025 - Presente",
      description:
        "Desenvolvimento e melhoria de aplicações web para plataforma EdTech, com foco em desempenho e acessibilidade (React/Next.js, TypeScript). Otimização de SEO e Core Web Vitals, implementação de componentes SEO-friendly e dados estruturados, colaborando com times multifuncionais usando GA4 e GSC para experimentos e métricas.",
      technologies: [
        "React",
        "Next.js",
        "TypeScript",
        "SEO",
        "Core Web Vitals",
        "GA4",
        "GSC",
      ],
      logo: "/assets/experience/gran_logo.png",
    },
    {
      id: 2,
      company: "Vivo (Telefônica Brasil) / Compass.UOL",
      role: "Software Developer",
      period: "Jun 2023 - Dec 2025",
      description:
        "Atuação via Compass.UOL para Vivo. Desenvolvimento de features para app com mais de 130 milhões de usuários, usando React, Next.js, TypeScript, integrações com GA4 e BFFs (Nest.js e Java Spring). Cobertura de testes superior a 90% com Jest, Cypress e Storybook (BDD).",
      technologies: [
        "React",
        "Next.js",
        "TypeScript",
        "GA4",
        "Nest.js",
        "Java Spring",
        "Jest",
        "Cypress",
        "Storybook",
      ],
      logo: "/assets/experience/vivo_logo.png",
    },
    {
      id: 3,
      company: "Usina de Laticinios Jussara S/A",
      role: "Data and Supply Chain Analyst",
      period: "May 2021 - Nov 2022",
      description:
        "Análise de custos da frota com Excel/VBA (redução mensal de ~R$200K). Otimização de rotas de coleta com GPS, adaptação contínua conforme mudanças de fornecedores. Gestão de equipe de 30 motoristas e 60 rotas de coleta.",
      technologies: ["Excel", "VBA", "GPS", "Python"],
      logo: "/assets/experience/jussara_logo.png",
    },
    {
      id: 4,
      company: "Inata Produtos Biológicos",
      role: "Quality Assurance Analyst",
      period: "Sep 2019 - Mar 2021",
      description:
        "Desenvolvimento e testes de regras de negócio para conformidade legal brasileira, otimizando processos antes baseados em papel. Análise de dados de produção, controle de qualidade e finanças, reduzindo custos de gestão ambiental. Garantia de execução adequada de procedimentos em laboratório e biotério.",
      technologies: ["QA", "Compliance", "Data Analysis"],
      logo: "/assets/experience/inata_logo.png",
    },
  ];
  
  // Usa dados da API se disponíveis, senão fallback
  const experience = experiencesData.length > 0 ? experiencesData.map((exp: any) => ({
    id: exp._id,
    company: exp.company,
    role: exp.role,
    period: `${new Date(exp.periodStart).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })} - ${exp.periodEnd ? new Date(exp.periodEnd).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : 'Present'}`,
    description: exp.description,
    technologies: exp.technologies || [],
    logo: exp.logo || '/assets/experience/default_logo.png',
  })) : experienceFallback;

  // Seções Sobre Mim
  const aboutMeSections = [
    {
      title: "Quem sou eu",
      content: [
        "Sou um Engenheiro de Software e Pesquisador em IA com paixão por resolver problemas complexos através da tecnologia. Meu objetivo é desenvolver soluções que possam impactar positivamente a vida das pessoas, combinando conhecimentos técnicos com uma visão humanizada da tecnologia.",
        "Nascido e criado em São Paulo, desde cedo demonstrei interesse por computadores e matemática, o que me levou a seguir carreira na área de tecnologia. Hoje, divido meu tempo entre projetos profissionais desafiadores e pesquisa acadêmica em inteligência artificial.",
      ],
      image: cassioImage,
    },
    {
      title: "Filosofia de Trabalho",
      content: [
        "Acredito que a melhor tecnologia é aquela que resolve problemas reais de forma elegante e eficiente. Valorizo código limpo, arquiteturas bem pensadas e processos ágeis que permitam entregas contínuas de valor.",
        "Como profissional, busco sempre o equilíbrio entre inovação e praticidade, entendendo que uma solução técnica só é realmente boa quando resolve o problema do usuário final.",
      ],
    },
    {
      title: "Fora do Trabalho",
      content: [
        "Quando não estou codificando ou pesquisando, gosto de praticar escalada, ler sobre filosofia e ciências, e explorar novos cafés pela cidade. Também sou um entusiasta de fotografia analógica e música instrumental.",
        "Acredito que experiências diversas fora da área técnica contribuem para uma visão mais ampla dos problemas e soluções no desenvolvimento de software.",
      ],
    },
  ];

  // Publicações (removidas conforme solicitação anterior)
  const publications: any[] = [];

  // Certificações (atualizado)
  const certifications = [
    {
      id: 1,
      name: "Oracle Certified Associate",
      issuer: "Oracle",
      date: "Sep 2025",
      credentialId: "102531165OFAASOFA",
      logo: "/assets/certifications/oracle_logo.png",
    },
    {
      id: 2,
      name: "AWS Certified Cloud Practitioner",
      issuer: "Amazon Web Services (AWS)",
      date: "Issued Apr 2023 · Expires Apr 2026",
      logo: "/assets/certifications/aws_logo.png",
    },
  ];

  const handleDownloadCV = () => {
    console.log("Download CV");
    // Implementação futura para download do CV
    window.open("/assets/cassio-takarada-cv.pdf", "_blank");
  };

  const viewProps: AboutViewProps = {
    isLoading: isLoading || educationLoading || experiencesLoading,
    education: [...education].sort((a, b) => {
      const parseDate = (period: string) => {
        // Examples: "Jul 2024 - Jul 2026", "2011 - 2019", "Jan 2015 - Dec 2015"
        const months: Record<string, number> = {
          Jan: 1,
          Feb: 2,
          Mar: 3,
          Apr: 4,
          May: 5,
          Jun: 6,
          Jul: 7,
          Aug: 8,
          Sep: 9,
          Oct: 10,
          Nov: 11,
          Dec: 12,
        };
        const [start] = period.split(" - ");
        const parts = start.split(" ");
        if (parts.length === 2) {
          const month = months[parts[0]] || 1;
          const year = parseInt(parts[1], 10);
          return new Date(year, month - 1, 1).getTime();
        }
        // If only year
        const yearOnly = parseInt(start, 10);
        return new Date(yearOnly, 0, 1).getTime();
      };
      return parseDate(b.period) - parseDate(a.period);
    }),
    experience: [...experience].sort((a, b) => {
      const parseDate = (period: string) => {
        // Examples: "Dec 2025 - Presente", "Jun 2023 - Dec 2025", "May 2021 - Nov 2022", "Sep 2019 - Mar 2021"
        const months: Record<string, number> = {
          Jan: 1,
          Feb: 2,
          Mar: 3,
          Apr: 4,
          May: 5,
          Jun: 6,
          Jul: 7,
          Aug: 8,
          Sep: 9,
          Oct: 10,
          Nov: 11,
          Dec: 12,
        };
        const [start] = period.split(" - ");
        const parts = start.split(" ");
        if (parts.length === 2) {
          const month = months[parts[0]] || 1;
          const year = parseInt(parts[1], 10);
          return new Date(year, month - 1, 1).getTime();
        }
        const yearOnly = parseInt(start, 10);
        return new Date(yearOnly, 0, 1).getTime();
      };
      return parseDate(b.period) - parseDate(a.period);
    }),
    aboutMeSections,
    publications,
    certifications,
    handleDownloadCV,
  };

  return viewProps;
}

export default AboutController;
